import { isDefined } from '@sgme/fp';
import type { ColDef, ValueFormatterFunc, ValueGetterParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { type IntlShape, useIntl } from 'react-intl';

import {
  AmountRenderer,
  DateRenderer,
  RateRenderer,
  StatusCellRenderer,
} from '@/App/Blotter/grids/commonCellRenderers.tsx';
import { dateFormatterBuilder, datetimeFormatterBuilder } from '@/App/Blotter/grids/commonFormatters.ts';
import {
  dateFilterDefinition,
  numberFilterDefinition,
  setFilterDefinition,
  textFilterDefinition,
} from '@/App/Blotter/grids/filterDefinitions.ts';
import { AmountDoneCellRenderer } from '@/App/Blotter/grids/order/AmountDoneCellRenderer.tsx';
import { CommandsCellRenderer } from '@/App/Blotter/grids/order/CommandsCellRenderer.tsx';
import { useUserContext } from '@/context/UserContext.tsx';
import { isAlgoOrderType, type OrderType } from '@/store/blotter/api/types/BlotterOrderEntryFromBackend.ts';
import type { BlotterOrderEntry } from '@/store/blotter/types/BlotterOrderEntry.ts';
import type { ISODate, ISODateTime } from '@/types/date.ts';

export function useColumnDefinitions() {
  const intl = useIntl();
  const { getCompanyName } = useUserContext();

  return useMemo<ColDef<BlotterOrderEntry>[]>(() => {
    const { formatMessage } = intl;
    const datetimeFormatter = datetimeFormatterBuilder<BlotterOrderEntry>(intl);
    return [
      {
        field: 'id',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.id' }),
        width: 150,
        lockPosition: true,
        ...textFilterDefinition,
      },
      {
        field: 'clientId',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.clientId' }),
        width: 200,
        ...textFilterDefinition,
        valueGetter: ({ data: { clientId } = {} }) => (clientId ? (getCompanyName(clientId) ?? clientId) : '-'),
      },
      {
        colId: 'actions',
        valueGetter: ({ data }: ValueGetterParams<BlotterOrderEntry>) => data,
        headerName: formatMessage({ id: 'blotter.tab.Order.column.actions' }),
        cellRenderer: CommandsCellRenderer,
        width: 140,
        filter: false,
        sortable: false,
      },
      {
        colId: 'amountDone',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.amountDone' }),
        width: 200,
        filter: false,
        sortable: false,
        cellRenderer: AmountDoneCellRenderer,
      },
      {
        field: 'currencyPair',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.currencyPair' }),
        width: 100,
        ...textFilterDefinition, // text filtering is more appropriate here rather than a set filter (possibly lots of different currency pairs in the grid)
      },
      {
        colId: 'expiry',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.expiry' }),
        valueFormatter: expiryFormatter(intl),
        width: 130,
        filter: false,
        sortable: false,
      },
      {
        field: 'notional',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.notional' }),
        cellRenderer: AmountRenderer,
        width: 120,
        ...numberFilterDefinition,
      },
      {
        field: 'notionalCurrency',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.notionalCurrency' }),
        width: 120,
        ...textFilterDefinition,
      },
      {
        field: 'limitPrice',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.limitPrice' }),
        cellRenderer: RateRenderer,
        width: 120,
      },
      {
        field: 'customerPrice',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.customerPrice' }),
        cellRenderer: RateRenderer,
        width: 120,
      },
      {
        field: 'orderType',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.orderType' }),
        width: 150,
        valueFormatter: ({ value }: { value: OrderType }) =>
          formatMessage({ id: `blotter.tab.Order.column.orderType.${value}` }),
        ...setFilterDefinition,
      },
      {
        field: 'status',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.status' }),
        cellRenderer: StatusCellRenderer,
        width: 130,
        ...setFilterDefinition,
      },
      {
        field: 'yourWay',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.yourWay' }),
        width: 120,
        ...setFilterDefinition,
      },
      {
        field: 'executionPrice',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.executionPrice' }),
        cellRenderer: AmountRenderer,
        width: 150,
        ...numberFilterDefinition,
      },
      {
        field: 'startTime',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.startTime' }),
        valueFormatter: datetimeFormatter,
        width: 150,
        ...dateFilterDefinition,
      },
      {
        field: 'endTime',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.endTime' }),
        valueFormatter: datetimeFormatter,
        width: 150,
        ...dateFilterDefinition,
      },
      {
        field: 'maturityDate',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.maturityDate' }),
        valueFormatter: dateFormatterBuilder(intl),
        width: 130,
        ...dateFilterDefinition,
      },
      {
        field: 'liquidityPool',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.liquidityPool' }),
        width: 150,
        ...textFilterDefinition, // unsure, depends on the real data, 'set' instead ?
      },
      {
        field: 'clippingMode',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.clippingMode' }),
        width: 150,
        ...textFilterDefinition, // unsure, depends on the real data, 'set' instead ?
      },
      {
        field: 'clipSize',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.clipSize' }),
        cellRenderer: AmountRenderer,
        width: 130,
        ...numberFilterDefinition,
      },
      {
        field: 'randomize',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.randomize' }),
        valueFormatter: yesOrEmptyFormatter(intl),
        cellDataType: 'text',
        width: 100,
        filter: false,
        sortable: false,
        cellClass: 'text-capitalize',
      },
      {
        field: 'swapPoints',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.swapPoints' }),
        cellRenderer: AmountRenderer,
        width: 130,
        ...numberFilterDefinition,
      },
      {
        field: 'forwardPrice',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.forwardPrice' }),
        cellRenderer: AmountRenderer,
        width: 130,
        ...numberFilterDefinition,
      },
      {
        field: 'remainingAmount',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.remainingAmount' }),
        cellRenderer: AmountRenderer,
        width: 150,
        ...numberFilterDefinition,
      },
      {
        field: 'averagePrice',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.averagePrice' }),
        cellRenderer: AmountRenderer,
        width: 140,
        ...numberFilterDefinition,
      },
      {
        field: 'noWorseThan',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.noWorseThan' }),
        cellRenderer: AmountRenderer,
        width: 130,
        ...numberFilterDefinition,
      },
      {
        field: 'spreadCapture',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.spreadCapture' }),
        valueFormatter: yesOrEmptyFormatter(intl),
        cellDataType: 'text',
        width: 100,
        filter: false,
        sortable: false,
        cellClass: 'text-capitalize',
      },
      {
        field: 'alphaSeeker',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.alphaSeeker' }),
        valueFormatter: yesOrEmptyFormatter(intl),
        cellDataType: 'text',
        width: 100,
        filter: false,
        sortable: false,
        cellClass: 'text-capitalize',
      },
      {
        field: 'speed',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.speed' }),
        width: 100,
        ...textFilterDefinition, // unsure, depends on the real data, 'set' instead ?
      },
      {
        field: 'fixingBenchmark',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.fixingBenchmark' }),
        width: 110,
        ...textFilterDefinition, // unsure, depends on the real data, 'set' instead ?
      },
      {
        field: 'fixingPlace',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.fixingPlace' }),
        width: 140,
        ...textFilterDefinition, // unsure, depends on the real data, 'set' instead ?
      },
      {
        field: 'fixingTime',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.fixingTime' }),
        width: 120,
        sortable: false,
        filter: false,
      },
      {
        field: 'fixingDateUtc',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.fixingDateUtc' }),
        cellRenderer: DateRenderer,
        width: 130,
        ...dateFilterDefinition,
      },
      {
        field: 'fixingPriceType',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.fixingPriceType' }),
        width: 120,
        ...textFilterDefinition, // unsure, depends on the real data, 'set' instead ?
      },
      {
        field: 'fixingMarginType',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.fixingMarginType' }),
        width: 140,
        ...textFilterDefinition, // unsure, depends on the real data, 'set' instead ?
      },
      {
        field: 'price',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.price' }),
        cellRenderer: AmountRenderer,
        width: 80,
        ...numberFilterDefinition,
      },
      {
        field: 'marginInBps',
        headerName: formatMessage({ id: 'blotter.tab.Order.column.marginInBps' }),
        cellRenderer: AmountRenderer,
        width: 80,
        ...numberFilterDefinition,
      },
    ];
  }, [getCompanyName]);
}

const expiryFormatter: (intl: IntlShape) => ValueFormatterFunc<BlotterOrderEntry, ISODateTime | ISODate> =
  ({ formatMessage, formatDate }) =>
  ({ data }) => {
    if (isDefined(data)) {
      if (data.goodTillCancelled) {
        if (isAlgoOrderType(data.orderType)) {
          return formatMessage({ id: 'blotter.tab.Order.column.expiry.goodTillDate' });
        }
        return formatMessage({ id: 'blotter.tab.Order.column.expiry.goodTillCancelled' });
      }
      if (isDefined(data.expiryDay) && isDefined(data.expiryTime)) {
        const datetime = `${data.expiryDay}T${data.expiryTime}Z`;
        return formatDate(datetime, { dateStyle: 'medium', timeStyle: 'short' });
      }
    }
    return '';
  };

const yesOrEmptyFormatter: (intl: IntlShape) => ValueFormatterFunc<BlotterOrderEntry, boolean | null> =
  ({ formatMessage }) =>
  ({ value }) =>
    value === true ? formatMessage({ id: 'yes' }) : '';
